import { createFileRoute, Link } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { AuthLayout, FormError } from '../components/auth/auth-layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ApiError, api } from '../lib/api';

export const Route = createFileRoute('/request-access')({ component: RequestAccess });

function RequestAccess() {
  const [form, setForm] = useState({ name: '', email: '', reason: '' });
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/access-requests', { body: form });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };
  if (done)
    return (
      <AuthLayout title="Request sent">
        <p className="text-sm text-muted-foreground">
          An admin will review your request. If it's approved, you'll get an email with sign-in instructions.
        </p>
        <Link to="/login" search={{ next: undefined }} className="text-sm font-medium underline">
          Back to sign in
        </Link>
      </AuthLayout>
    );
  return (
    <AuthLayout title="Request access">
      <form className="space-y-4" onSubmit={submit}>
        <div className="space-y-1.5">
          <label htmlFor="name" className="text-sm font-medium">
            Your name
          </label>
          <Input id="name" autoComplete="name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="email" className="text-sm font-medium">
            Work email
          </label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="reason" className="text-sm font-medium">
            Why do you need access? <span className="font-normal text-muted-foreground">(optional)</span>
          </label>
          <textarea
            id="reason"
            rows={3}
            value={form.reason}
            onChange={e => setForm({ ...form, reason: e.target.value })}
            className="w-full rounded-md border border-input bg-surface p-2 text-sm"
          />
        </div>
        <FormError message={error} />
        <Button type="submit" className="w-full" disabled={busy}>
          Send request
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          Already have access?{' '}
          <Link to="/login" search={{ next: undefined }} className="font-medium text-foreground underline">
            Sign in
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
